import { ApiError } from "@/lib/api/error";
import { showToast } from "@/components/ui/sonner";

export const MAX_BULK_PIPELINE_SELECTION = 1000;
export const BULK_SELECTION_SOFT_WARN_AT = 900;

export type ClampResult = {
  ids: string[];
  truncated: boolean;
  omitted: number;
};

export const BULK_SELECTION_COPY = {
  softApproaching: "Approaching 1,000 limit",
  softAtCap: "1,000 limit reached",
  selectAllLoadingOverCap: "Selecting the first 1,000 candidates…",
  selectAllTruncated: "Only the first 1,000 candidates were selected",
  partialOmitted: (omitted: number) =>
    `${omitted} candidates weren't selected — 1,000 limit reached`,
  moveCapExplain: "You can move up to 1,000 candidates at a time",
  archiveCapExplain: "You can archive up to 1,000 candidates at a time",
} as const;

export function clampBulkSelectionIds(
  existing: Iterable<string>,
  incoming: string[],
  max = MAX_BULK_PIPELINE_SELECTION,
): ClampResult {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const id of existing) {
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  let omitted = 0;
  for (const id of incoming) {
    if (seen.has(id)) continue;
    if (ids.length >= max) {
      omitted += 1;
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  // If existing alone was already over max (shouldn't happen), trim.
  if (ids.length > max) {
    omitted += ids.length - max;
    ids.length = max;
  }
  return { ids, truncated: omitted > 0, omitted };
}

export function replaceBulkSelectionIds(
  ids: string[],
  max = MAX_BULK_PIPELINE_SELECTION,
): ClampResult {
  return clampBulkSelectionIds([], ids, max);
}

export function isBulkCandidateIdsValidationError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.code !== "VALIDATION_ERROR") return false;
  const errors = err.detail?.errors;
  if (!Array.isArray(errors)) return false;
  return errors.some((e) => {
    if (!e || typeof e !== "object") return false;
    const field = (e as { field?: unknown }).field;
    return typeof field === "string" && field.endsWith("job_candidate_ids");
  });
}

export function descriptionForPipelineBulkError(
  err: unknown,
  action: "move" | "archive",
): string {
  if (isBulkCandidateIdsValidationError(err)) {
    return action === "move"
      ? BULK_SELECTION_COPY.moveCapExplain
      : BULK_SELECTION_COPY.archiveCapExplain;
  }
  return err instanceof Error ? err.message : "An unexpected error occurred.";
}

export type TruncateToastMode = "select-all" | "partial" | "toggle";

export function toastIfSelectionTruncated(
  result: ClampResult,
  mode: TruncateToastMode,
): void {
  if (!result.truncated || mode === "toggle") return;
  if (mode === "select-all") {
    showToast.warning(BULK_SELECTION_COPY.selectAllTruncated);
    return;
  }
  showToast.warning(BULK_SELECTION_COPY.partialOmitted(result.omitted));
}

export function bulkSelectionSoftWarnLabel(count: number): string | null {
  if (count >= MAX_BULK_PIPELINE_SELECTION) return BULK_SELECTION_COPY.softAtCap;
  if (count >= BULK_SELECTION_SOFT_WARN_AT) return BULK_SELECTION_COPY.softApproaching;
  return null;
}
